import React from 'react';
import { Modal, Form, Input, Select, DatePicker, InputNumber, Switch, message } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import dayjs from 'dayjs';
import { CompetitionType, CreateCompetitionRequest } from '../../../core/competition/types';
import { subjectOptions, competitionTypeOptions, gradeOptions } from '../../../core/utils/helpers';

const { Option } = Select;

interface CreateCompetitionModalProps {
    visible: boolean;
    onCancel: () => void;
    onSubmit: (values: CreateCompetitionRequest) => void;
    loading?: boolean | false;
}

const CreateCompetitionModal: React.FC<CreateCompetitionModalProps> = ({
    visible,
    onCancel,
    onSubmit,
    loading = false,
}) => {
    const [form] = useForm();

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            const formattedValues: CreateCompetitionRequest = {
                ...values,
                date: values.date.format('YYYY-MM-DD'),
                imported: values.imported || false,
            };
            onSubmit(formattedValues);
        } catch (error) {
            message.error('Prašome užpildyti visus privalomus laukus');
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="Sukurti naują konkursą"
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            confirmLoading={loading}
            width={600}
            destroyOnClose>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    imported: false,
                    grade_from: 1,
                    grade_to: 12,
                    participants_total: 0,
                }}>
                <Form.Item
                    name="title"
                    label="Pavadinimas"
                    rules={[{ required: true, message: 'Prašome įvesti pavadinimą' }]}>
                    <Input placeholder="Įveskite konkurso pavadinimą" />
                </Form.Item>

                <Form.Item
                    name="subject"
                    label="Dalykas"
                    rules={[{ required: true, message: 'Prašome įvesti dalyką' }]}>
                    <Select placeholder="Pasirinkite dalyką">
                        {subjectOptions.map(subject => (
                            <Option key={subject.value} value={subject.value}>
                                {subject.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="type"
                    label="Tipas"
                    rules={[{ required: true, message: 'Prašome pasirinkti tipą' }]}>
                    <Select placeholder="Pasirinkite konkurso tipą">
                        {competitionTypeOptions.map(type => (
                            <Option key={type.value} value={type.value}>
                                {type.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="date"
                    label="Data"
                    rules={[{ required: true, message: 'Prašome pasirinkti datą' }]}>
                    <DatePicker
                        style={{ width: '100%' }}
                        placeholder="Pasirinkite datą"
                        format="YYYY-MM-DD" />
                </Form.Item>

                <Form.Item
                    name="grade_from"
                    label="Konkurso klasė nuo"
                    rules={[{ required: true, message: 'Prašome pasirinkti konkurso klasę nuo' }]}>
                    <Select placeholder="Pasirinkite klasę nuo">
                        {gradeOptions.map(grade => (
                            <Option key={grade.value} value={grade.value}>
                                {grade.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="grade_to"
                    label="Konkurso klasė iki"
                    rules={[{ required: true, message: 'Prašome pasirinkti konkurso klasę iki' }]}>
                    <Select placeholder="Pasirinkite klasę iki">
                        {gradeOptions.map(grade => (
                            <Option key={grade.value} value={grade.value}>
                                {grade.label}
                            </Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="participants_total"
                    label="Dalyvių skaičius"
                    rules={[{ required: true, message: 'Prašome įvesti dalyvių skaičių' }]}>
                    <InputNumber
                        min={0}
                        style={{ width: '100%' }}
                        placeholder="Dalyvių skaičius" />
                </Form.Item>

                <Form.Item
                    name="organizer"
                    label="Organizatorius"
                    rules={[{ required: true, message: 'Prašome įvesti organizatorių' }]}>
                    <Input placeholder="Įveskite organizatorių" />
                </Form.Item>
            </Form>
        </Modal>
    );
};

CreateCompetitionModal.defaultProps = {
    loading: false,
};

export default CreateCompetitionModal;
